import React, { useState, useEffect, useContext } from "react";
import {
  Autocomplete,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Box,
  IconButton,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import RichTextEditor from "../components/RichTextEditor";
import ImageThumb from "../components/ImageThumb";

const TAGLINE_MAX = 90;
const GALLERY_MAX = 4;

const paperSx = {
  width: "100%",
  backgroundColor: "background.paper",
  mb: 2,
  p: 3,
};


function ImageRow({ alt, image, onChange }) {
  const handleChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onChange({ file, preview: URL.createObjectURL(file) });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <ImageThumb src={image?.preview} alt={alt} size={64} zoomable />
      <Button component="label" size="small" sx={{ minWidth: 0 }}>
        {image ? "Change image" : "Upload image"}
        <input type="file" accept="image/*" hidden onChange={handleChange} />
      </Button>
      <IconButton size="small" disabled={!image} onClick={() => onChange(null)}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// producerId: null to create, or the id of the producer to edit.
export default function ProducerForm({ producerId = null, onClose = () => {} }) {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [description, setDescription] = useState("");
  const [collection, setCollection] = useState(null);
  const [logo, setLogo] = useState(null);

  const [gallery, setGallery] = useState(Array(GALLERY_MAX).fill(null));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!producerId);
  const isEdit = !!producerId;

  const [collectionOptions, setCollectionOptions] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState("");

  // Debounced search; `active` drops responses from searches that were superseded.
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const response = await fetchWithInterceptor(
          `/api/producers/collections?search=${encodeURIComponent(collectionSearch)}`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-access-token": authContext.getToken(),
            },
          }
        );
        if (!response?.ok || !active) return;
        const data = await response.json();
        if (active) setCollectionOptions(data.rows || []);
      } catch (err) {
        console.error(err);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionSearch]);

  // Edit: load the full producer from Shopify (only happens when opening it).
  useEffect(() => {
    if (!producerId) return;
    const loadProducer = async () => {
      try {
        const response = await fetchWithInterceptor(`/api/producers/${producerId}`, {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        });
        if (!response.ok) throw new Error("Failed to load producer");
        const data = await response.json();
        setName(data.name);
        setTagline(data.tagline);
        setStatus(data.status);
        setDescription(data.description);
        setCollection(data.collection);
        setLogo(data.logo ? { id: data.logo.id, preview: data.logo.url } : null);
        const images = data.gallery.map((image) => ({ id: image.id, preview: image.url }));
        // Fill the empty positions with null.
        setGallery([...images, ...Array(GALLERY_MAX - images.length).fill(null)]);
      } catch (err) {
        console.error(err);
        appContext.showMessage("Error loading producer", "error");
        onClose();
      } finally {
        setLoading(false);
      }
    };
    loadProducer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producerId]);

  const handleGalleryChange = (index, image) => {
    setGallery((prev) => prev.map((item, i) => (i === index ? image : item)));
  };

  const isValid = name.trim() && description;

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("name", name);
      formData.append("tagline", tagline);
      formData.append("status", status);
      formData.append("description", description);
      if (collection) formData.append("collectionId", collection.id);
      // New images go as files; on edit, unchanged ones are sent by id to keep them.
      if (logo?.file) formData.append("logo", logo.file);
      else if (logo?.id) formData.append("logoId", logo.id);
      const images = gallery.filter(Boolean);
      formData.append(
        "galleryItems",
        JSON.stringify(images.map((image) => (image.file ? { upload: true } : { id: image.id })))
      );
      images.filter((image) => image.file).forEach((image) => formData.append("gallery", image.file));

      const response = await fetchWithInterceptor(isEdit ? `/api/producers/${producerId}` : "/api/producers", {
        method: isEdit ? "PUT" : "POST",
        headers: { "x-access-token": authContext.getToken() },
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Failed to save producer");
      }
      appContext.showMessage(isEdit ? "Producer updated successfully" : "Producer created successfully", "success");
      onClose();
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error saving producer", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", ml: 2, mt: 3, mb: 1 }}>
        <IconButton onClick={() => onClose()} disabled={saving} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {isEdit
            ? 'Edit this producer. Changes are saved to its "Producer" metaobject in Shopify.'
            : 'Create a new producer. It will be created as a "Producer" metaobject in Shopify.'}
        </Typography>
      </Box>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        {/* Details */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Producer Details
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Name"
                fullWidth
                required
                autoComplete="off"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={
                  collection && !collectionOptions.some((option) => option.id === collection.id)
                    ? [collection, ...collectionOptions]
                    : collectionOptions
                }
                value={collection}
                getOptionLabel={(option) => option?.title ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options) => options}
                onInputChange={(_, value) => setCollectionSearch(value)}
                onChange={(_, value) => setCollection(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Collection" placeholder="Search collections" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <TextField
                label="Tagline"
                fullWidth
                autoComplete="off"
                value={tagline}
                onChange={(e) => setTagline(e.target.value.slice(0, TAGLINE_MAX))}
                helperText={`${tagline.length}/${TAGLINE_MAX}`}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Description */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Description *
          </Typography>
          <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 2 }}>
            <RichTextEditor basic value={description} onChange={setDescription} />
          </Box>
        </Paper>

        {/* Images */}
        <Paper sx={paperSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Images
          </Typography>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Logo
              </Typography>
              <ImageRow alt="Logo" image={logo} onChange={setLogo} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Gallery ({gallery.filter(Boolean).length}/{GALLERY_MAX})
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, columnGap: 3, rowGap: 1.5 }}>
                {gallery.map((image, index) => (
                  <ImageRow
                    key={index}
                    alt={`Gallery ${index + 1}`}
                    image={image}
                    onChange={(value) => handleGalleryChange(index, value)}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
          <Button onClick={() => onClose()} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!isValid || saving}>
            {isEdit ? (saving ? "Saving..." : "Save changes") : saving ? "Creating..." : "Create producer"}
          </Button>
        </Box>
      </Container>
    </>
  );
}
